import { asAmount, OrderDirection } from "@goodfill/domain-properties";
import {
  TradeValueComponent,
  SettlementValueComponent,
  FinalCommissionAmountComponent,
  AccruedInterestAmountComponent,
  SettlementDateComponent,
  TradeDateComponent,
} from "@goodfill/domain-properties-components";
import { sortByKeys, SortColumn } from "@goodfill/sort";
import { Button } from "@goodfill/ui-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { route } from "../../../../routes";
import { GridTableFilterRow } from "../../../../ui-components/GridTable/GridTableFilterRow";
import {
  defaultPortfolioTradeHistoryFilterConstraints,
  PortfolioTradeHistoryFilter,
  portfolioTradeHistoryFilters,
} from "./PortfolioTradeHistoryFilter";
import { PresentedTradeTicket } from "./PresentedTradeTicket";
import { getTradeHistoryComparator } from "./TradeHistoryComparator";
import DataGrid, { Column } from "react-data-grid";
import AutoSizer from "react-virtualized-auto-sizer";
import { mapSortColumnFromGrid } from "../../../../sort/mapSortColumnFromGrid";
import { DateRangeFilter } from "../../../../filters/DateTimeRangeFilter";
import { applyFilters } from "../../../../filters/filters";
import { FilterSimpleSelectBox } from "../../../../filters/FilterSimpleSelectBox";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";
import { getSortedIsins, IsinColumnDefinition } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  getSortedIssuerIds,
  IssuerIdColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { MaturityDateColumnDefinition } from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { MoodysRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  getSortedPaymentRanks,
  PaymentRankColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import { SpRatingColumnDefinition } from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import {
  getSortedTickers,
  TickerColumnDefinition,
} from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import { FaceAmountColumnDefinition } from "../../../DataGridFilteredColumns/order/FaceAmountColumnDefinition";
import {
  getSortedOrderTicketIds,
  OrderTicketIdColumnDefinition,
} from "../../../DataGridFilteredColumns/order/OrderTicketIdColumnDefinition";
import {
  CouponPaymentColumnDefinition,
  getSortedBondCouponPayments,
} from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";
import {
  getSortedTradeTicketIds,
  TradeTicketIdColumnDefinition,
} from "../../../DataGridFilteredColumns/tradeTicket/TradeTicketIdColumnDefinition";
import { PostFeePriceColumnDefinition } from "../../../DataGridFilteredColumns/order/PostFeePriceColumnDefinition";
import { TradeValueColumnDefinition } from "../../../DataGridFilteredColumns/tradeTicket/TradeValueColumnDefinition";
import { SettlementAmountColumnDefinition } from "../../../DataGridFilteredColumns/tradeTicket/SettlementAmountValueColumnDefinition";
import { FinalCommissionAmountColumnDefinition } from "../../../DataGridFilteredColumns/tradeTicket/FinalCommissionAmountColumnDefinition";

type SortableKeys = keyof PresentedTradeTicket;

export const TradeHistoryTable: React.FC<{ tradeTickets: PresentedTradeTicket[] }> = ({ tradeTickets }) => {
  const [criteria, setCriteria] = useState(defaultPortfolioTradeHistoryFilterConstraints);
  const navigate = useNavigate();
  const [sortColumns, setSortColumns] = useState<readonly SortColumn<SortableKeys>[]>([]);

  const onFilterChange = <T extends keyof PortfolioTradeHistoryFilter>(
    key: T,
    value: PortfolioTradeHistoryFilter[T],
  ) => setCriteria({ ...criteria, [key]: value });

  const tableDefinition: Array<
    Column<PresentedTradeTicket> & {
      key: keyof PresentedTradeTicket | "_detail";
    }
  > = useMemo(() => {
    const issuerIds = getSortedIssuerIds("issuerId", tradeTickets);
    const tickers = getSortedTickers("ticker", tradeTickets);
    const paymentRanks = getSortedPaymentRanks("paymentRank", tradeTickets);
    const orderTicketIds = getSortedOrderTicketIds("orderTicketId", tradeTickets);
    const isins = getSortedIsins("bondId", tradeTickets);
    const couponPayments = getSortedBondCouponPayments("couponPayment", tradeTickets);
    const tradeTicketIds = getSortedTradeTicketIds("tradeTicketId", tradeTickets);

    return [
      {
        key: "_detail",
        name: "",
        sortable: false,
        formatter: ({ row }) => (
          <Button
            fill
            size="small"
            uppercase={true}
            primary={true}
            onClick={() => navigate(route.tradeTicketDetail(row.tradeTicketId))}
          >
            Detail
          </Button>
        ),
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      {
        key: "tradeDate",
        name: "Trade Date",
        sortable: true,
        formatter: ({ row }) => (
          <TradeDateComponent value={row.tradeDate} timezone={row.bondTimezone} showTimezone={true} />
        ),
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <DateRangeFilter
              timezone={TIMEZONE_NEY_YORK}
              small
              value={criteria.tradeDateRange}
              onChange={(value) => onFilterChange("tradeDateRange", value)}
            />
          </GridTableFilterRow>
        ),
        width: 320,
      },
      {
        key: "settlementDate",
        name: "Settlement Date",
        sortable: true,
        formatter: ({ row }) => (
          <SettlementDateComponent value={row.settlementDate} timezone={row.bondTimezone} showTimezone={true} />
        ),
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      {
        key: "direction",
        name: "Buy/Sell",
        sortable: true,
        formatter: ({ row }) => <>{row.direction}</>,
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => (
          <GridTableFilterRow header={header}>
            <FilterSimpleSelectBox
              small
              options={[OrderDirection.Buy, OrderDirection.Sell]}
              value={`${criteria.direction}`}
              onChange={(value) => onFilterChange("direction", value !== null ? (value as OrderDirection) : null)}
            />
          </GridTableFilterRow>
        ),
        width: 150,
      },
      TickerColumnDefinition("ticker", criteria, onFilterChange, tickers),
      IssuerIdColumnDefinition("issuerId", criteria, onFilterChange, issuerIds),
      PaymentRankColumnDefinition("paymentRank", criteria, onFilterChange, paymentRanks),
      TradeTicketIdColumnDefinition("tradeTicketId", criteria, onFilterChange, tradeTicketIds),
      OrderTicketIdColumnDefinition("orderTicketId", criteria, onFilterChange, orderTicketIds),
      IsinColumnDefinition("bondId", criteria, onFilterChange, isins),
      CouponPaymentColumnDefinition("couponPayment", criteria, onFilterChange, couponPayments),
      MaturityDateColumnDefinition("maturityDate", criteria, onFilterChange),
      FaceAmountColumnDefinition("amount", criteria, onFilterChange),
      PostFeePriceColumnDefinition("loadedTradePrice", criteria, onFilterChange),
      TradeValueColumnDefinition("tradeValue", criteria, onFilterChange),
      {
        key: "accruedInterest",
        name: "Accrued Interest",
        sortable: true,
        formatter: ({ row }) => (
          <AccruedInterestAmountComponent value={asAmount(row.accruedInterest.mul(row.amount))} />
        ),
        headerCellClass: "GridTableFilterRow__header-cell",
        headerRenderer: (header) => <GridTableFilterRow header={header} />,
        width: 150,
      },
      SettlementAmountColumnDefinition("settlementValue", criteria, onFilterChange),
      FinalCommissionAmountColumnDefinition("finalCommissionAmount", criteria, onFilterChange),
      MoodysRatingColumnDefinition("bondRatingMoodys", criteria, onFilterChange),
      SpRatingColumnDefinition("bondRatingSp", criteria, onFilterChange),
    ];
  }, [criteria, onFilterChange, tradeTickets, navigate]);

  const filteredRows = applyFilters(tradeTickets, portfolioTradeHistoryFilters, criteria);

  // TODO: abstract sortedRows/summaryRows into useSortedRows(rows) and useSummaryRows(rows), respectively
  const sortedRows = useMemo(
    () => sortByKeys(filteredRows, sortColumns, getTradeHistoryComparator),
    [filteredRows, sortColumns],
  );

  const isDark = false;

  return (
    <AutoSizer disableHeight>
      {({ height, width }) => (
        <DataGrid
          className={`rdg-copyable ${isDark ? "rdg-dark" : "rdg-light"} OrderListTable`}
          columns={tableDefinition}
          rows={sortedRows}
          headerRowHeight={75}
          sortColumns={sortColumns}
          onSortColumnsChange={(columns) => setSortColumns(columns.map(mapSortColumnFromGrid<SortableKeys>))}
          style={{ height: 700, width }}
        />
      )}
    </AutoSizer>
  );
};
